import React, { useState, useMemo } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Layers,
  Sparkles,
  Copy,
  Check,
  Eye,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Download,
  AlertCircle,
} from 'lucide-react';
import { ReconciledItem } from '../../types/reconciliation';
import { useReconciliation } from '../../context/ReconciliationContext';
import { StatusBadge } from '../common/Badge';

type SortField =
  | 'paymentId'
  | 'customerName'
  | 'razorpayAmount'
  | 'erpTotalAmount'
  | 'differenceAmount'
  | 'billCount'
  | 'paymentDate'
  | 'status';

export const MasterTable: React.FC = () => {
  const {
    filteredItems,
    setSelectedItemForBills,
    setSelectedItemForAi,
    isProcessing,
  } = useReconciliation();

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('paymentId');
  const [sortAsc, setSortAsc] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Column Visibility state
  const [showColumnChooser, setShowColumnChooser] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    paymentId: true,
    customerName: true,
    razorpayAmount: true,
    erpTotalAmount: true,
    differenceAmount: true,
    billCount: true,
    billNumbers: true,
    paymentDate: true,
    refundStatus: true,
    refundAmount: true,
    status: true,
    actions: true,
  });

  // Copied toast state
  const [copiedPid, setCopiedPid] = useState<string | null>(null);

  const copyToClipboard = (pid: string) => {
    navigator.clipboard.writeText(pid);
    setCopiedPid(pid);
    setTimeout(() => setCopiedPid(null), 2000);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      let valA: any = a[sortField];
      let valB: any = b[sortField];

      if (typeof valA === 'string') {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (valA < valB) return sortAsc ? -1 : 1;
      if (valA > valB) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [filteredItems, sortField, sortAsc]);

  // Paginated items
  const totalPages = Math.max(Math.ceil(sortedItems.length / pageSize), 1);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedItems.slice(start, start + pageSize);
  }, [sortedItems, currentPage, pageSize]);

  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 opacity-60" />;
    }
    return sortAsc ? (
      <ArrowUp className="w-3.5 h-3.5 text-brand-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-brand-600" />
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col">
      {/* Table Action Bar */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-850">
        <div>
          <h3 className="font-bold text-sm text-slate-900 dark:text-white">
            Master Reconciliation Ledger
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Consolidated comparison of Razorpay gateway receipts against aggregated ERP multi-bill postings.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Column Visibility Chooser */}
          <div className="relative">
            <button
              onClick={() => setShowColumnChooser(!showColumnChooser)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>Columns</span>
            </button>

            {showColumnChooser && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl z-20 p-3 space-y-1 text-xs">
                <span className="font-bold text-slate-800 dark:text-slate-200 block mb-2">
                  Toggle Columns
                </span>
                {Object.keys(visibleColumns).map((col) => (
                  <label
                    key={col}
                    className="flex items-center gap-2 p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-slate-700 dark:text-slate-300"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col]}
                      onChange={() => toggleColumn(col)}
                      className="rounded text-brand-600 focus:ring-brand-500"
                    />
                    <span className="capitalize">{col.replace(/([A-Z])/g, ' $1')}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Rows per page selector */}
          <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <span>Show:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 font-mono text-xs"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Table Viewport with Sticky Header */}
      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 select-none shadow-xs font-semibold">
            <tr>
              {visibleColumns.paymentId && (
                <th
                  onClick={() => handleSort('paymentId')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Payment ID</span>
                    {renderSortIcon('paymentId')}
                  </div>
                </th>
              )}

              {visibleColumns.customerName && (
                <th
                  onClick={() => handleSort('customerName')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Customer Name</span>
                    {renderSortIcon('customerName')}
                  </div>
                </th>
              )}

              {visibleColumns.razorpayAmount && (
                <th
                  onClick={() => handleSort('razorpayAmount')}
                  className="py-3 px-4 text-right cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center justify-end gap-1.5">
                    <span>Razorpay Amt</span>
                    {renderSortIcon('razorpayAmount')}
                  </div>
                </th>
              )}

              {visibleColumns.erpTotalAmount && (
                <th
                  onClick={() => handleSort('erpTotalAmount')}
                  className="py-3 px-4 text-right cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center justify-end gap-1.5">
                    <span>ERP Total Amt</span>
                    {renderSortIcon('erpTotalAmount')}
                  </div>
                </th>
              )}

              {visibleColumns.differenceAmount && (
                <th
                  onClick={() => handleSort('differenceAmount')}
                  className="py-3 px-4 text-right cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center justify-end gap-1.5">
                    <span>Difference</span>
                    {renderSortIcon('differenceAmount')}
                  </div>
                </th>
              )}

              {visibleColumns.billCount && (
                <th
                  onClick={() => handleSort('billCount')}
                  className="py-3 px-4 text-center cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span>Bills</span>
                    {renderSortIcon('billCount')}
                  </div>
                </th>
              )}

              {visibleColumns.billNumbers && (
                <th className="py-3 px-4">ERP Bill References</th>
              )}

              {visibleColumns.paymentDate && (
                <th
                  onClick={() => handleSort('paymentDate')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Payment Date</span>
                    {renderSortIcon('paymentDate')}
                  </div>
                </th>
              )}

              {visibleColumns.refundStatus && (
                <th className="py-3 px-4 text-center">Refund</th>
              )}

              {visibleColumns.refundAmount && (
                <th className="py-3 px-4 text-right">Refund Amt</th>
              )}

              {visibleColumns.status && (
                <th
                  onClick={() => handleSort('status')}
                  className="py-3 px-4 cursor-pointer hover:bg-slate-200/70 dark:hover:bg-slate-700 transition"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Status</span>
                    {renderSortIcon('status')}
                  </div>
                </th>
              )}

              {visibleColumns.actions && (
                <th className="py-3 px-4 text-right">Actions</th>
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200 font-normal">
            {paginatedItems.length === 0 ? (
              <tr>
                <td colSpan={12} className="py-12 text-center text-slate-400 dark:text-slate-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No transactions found matching the selected filter criteria.
                </td>
              </tr>
            ) : (
              paginatedItems.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/60 transition group"
                >
                  {visibleColumns.paymentId && (
                    <td className="py-3 px-4 font-mono font-semibold whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-900 dark:text-white">{item.paymentId}</span>
                        <button
                          onClick={() => copyToClipboard(item.paymentId)}
                          className="text-slate-400 hover:text-brand-600 transition"
                          title="Copy Payment ID"
                        >
                          {copiedPid === item.paymentId ? (
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                          )}
                        </button>
                      </div>
                    </td>
                  )}

                  {visibleColumns.customerName && (
                    <td className="py-3 px-4 max-w-xs truncate" title={item.customerName}>
                      {item.customerName}
                    </td>
                  )}

                  {visibleColumns.razorpayAmount && (
                    <td className="py-3 px-4 text-right font-mono font-medium whitespace-nowrap">
                      ₹{item.razorpayAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  )}

                  {visibleColumns.erpTotalAmount && (
                    <td className="py-3 px-4 text-right font-mono font-medium whitespace-nowrap text-slate-900 dark:text-slate-100">
                      ₹{item.erpTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  )}

                  {visibleColumns.differenceAmount && (
                    <td className="py-3 px-4 text-right font-mono font-semibold whitespace-nowrap">
                      <span
                        className={
                          Math.abs(item.differenceAmount) < 0.01
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : item.differenceAmount > 0
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-purple-600 dark:text-purple-400'
                        }
                      >
                        {item.differenceAmount > 0 ? '+' : ''}
                        ₹{item.differenceAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                  )}

                  {visibleColumns.billCount && (
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => setSelectedItemForBills(item)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold transition ${
                          item.billCount > 1
                            ? 'bg-brand-50 text-brand-700 border border-brand-200 dark:bg-brand-950 dark:text-brand-300 dark:border-brand-800 hover:bg-brand-100'
                            : item.billCount === 1
                            ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                            : 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300'
                        }`}
                        title="Click to view linked ERP bill breakdown"
                      >
                        <Layers className="w-3 h-3" />
                        <span>{item.billCount}</span>
                      </button>
                    </td>
                  )}

                  {visibleColumns.billNumbers && (
                    <td className="py-3 px-4 max-w-xs truncate font-mono text-[11px] text-slate-500 dark:text-slate-400">
                      {item.billNumbers.length > 0 ? item.billNumbers.join(', ') : 'None'}
                    </td>
                  )}

                  {visibleColumns.paymentDate && (
                    <td className="py-3 px-4 whitespace-nowrap text-slate-500 dark:text-slate-400">
                      {item.paymentDate}
                    </td>
                  )}

                  {visibleColumns.refundStatus && (
                    <td className="py-3 px-4 text-center">
                      {item.refundStatus === 'Refunded' ? (
                        <span className="text-[11px] font-semibold text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950 px-2 py-0.5 rounded-full">
                          Refunded
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  )}

                  {visibleColumns.refundAmount && (
                    <td className="py-3 px-4 text-right font-mono text-slate-500">
                      {item.refundAmount > 0
                        ? `₹${item.refundAmount.toLocaleString('en-IN')}`
                        : '-'}
                    </td>
                  )}

                  {visibleColumns.status && (
                    <td className="py-3 px-4 whitespace-nowrap">
                      <StatusBadge status={item.status} size="sm" />
                    </td>
                  )}

                  {visibleColumns.actions && (
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setSelectedItemForBills(item)}
                          className="p-1.5 text-slate-500 hover:text-brand-600 dark:hover:text-brand-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                          title="View ERP Split Bills Breakdown"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setSelectedItemForAi(item)}
                          className="p-1.5 text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                          title="AI Mismatch Diagnostic"
                        >
                          <Sparkles className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-850">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Showing <span className="font-semibold text-slate-800 dark:text-slate-200">
            {sortedItems.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}
          </span>{' '}
          to{' '}
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            {Math.min(currentPage * pageSize, sortedItems.length)}
          </span>{' '}
          of <span className="font-semibold text-slate-800 dark:text-slate-200">{sortedItems.length}</span> entries
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="p-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-mono font-medium px-2 text-slate-700 dark:text-slate-300">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
